import { useId } from 'react';
import { cn } from '@cyphex/ui';
import type { LucideIcon } from 'lucide-react';

export interface ToggleRowProps {
  label: string;
  description?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  icon?: LucideIcon;
  disabled?: boolean;
}

/**
 * Labelled switch row used across the notification settings panel.
 * `role="switch"` on a real checkbox keeps it keyboard- and SR-accessible.
 */
export function ToggleRow({
  label, description, checked, onCheckedChange, icon: Icon, disabled = false,
}: ToggleRowProps) {
  const id = useId();

  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <label htmlFor={id} className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5">
        {Icon ? (
          <Icon size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
        ) : null}
        <span className="min-w-0">
          <span className="block text-md font-medium text-foreground">{label}</span>
          {description ? (
            <span className="mt-0.5 block text-sm text-muted-foreground">{description}</span>
          ) : null}
        </span>
      </label>

      <span
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-pill transition-colors duration-fast',
          checked ? 'bg-primary' : 'bg-border-strong',
          disabled && 'opacity-50',
        )}
      >
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onCheckedChange(event.target.checked)}
          className="absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none ml-0.5 inline-block h-4 w-4 rounded-pill bg-card shadow-xs transition-transform duration-fast',
            checked && 'translate-x-4',
          )}
        />
      </span>
    </div>
  );
}
